// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import PropTypes from 'prop-types'
import { Trans, useTranslation } from 'react-i18next'
import { Box } from 'grommet'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { useReactOidc } from '@axa-fr/react-oidc-context'
import { useFlags } from 'launchdarkly-react-client-sdk'

import {
  Button,
  Loader,
  ModalDialog,
  ModalFooter,
  ModalHeader,
  Typography
} from '../../../../components'
import { del, get, getErrorMessage } from '../../../../utils/api-utils'
import { isAuthzV2London } from '../../../../utils/feature-flag-utils'

const RemoveUserModal = ({
  onSetOpen,
  userToRemove,
  setNotificationInfo,
  retrievedData
}) => {
  const navigate = useNavigate()
  const { oidcUser } = useReactOidc()
  const { t } = useTranslation(['iam', 'common'])
  const [loading, setLoading] = useState(false)
  const [workspacesCount, setWorkspacesCount] = useState(0)
  const LDFlags = useFlags()
  const isV2London = isAuthzV2London(LDFlags)

  const closeModal = () => {
    onSetOpen(false)
  }

  const userName = userToRemove?.displayName || userToRemove?.userName
  const workspaceName = userToRemove?.workspaceName
  const hasInheritedRoleAssignment = retrievedData?.some(
    // A source id means user inherited RA from user group
    (roleAssignment) => !isEmpty(roleAssignment?.source_id)
  )
  const isOwnAccount = userToRemove?.userName === oidcUser?.profile?.email

  useEffect(() => {
    if (isV2London) {
      setLoading(true)
      get(`/organizations/v2alpha1/workspaces?offset=0&limit=2`)
        .then((response) => {
          setWorkspacesCount(response?.data?.total)
        })
        .catch((error) => {
          setNotificationInfo(getErrorMessage(error, t), 'error')
        })
        .finally(() => {
          setLoading(false)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const removeUser = async () => {
    setLoading(true)
    // eslint-disable-next-line prettier/prettier
    del(
      `/workspaces/v2alpha1/workspaces/${userToRemove?.workspaceId}/users/${userToRemove?.id}`
    ).then(
      () => {
        navigate('/manage-account/identity/users', {
          state: {
            notificationText: t(
              'iam:igc_users.remove_workspace_user_success_msg_v2',
              {
                userName,
                workspaceName
              }
            ),
            notificationTitle: t('iam:igc_users.remove_user_success_title')
          }
        })
      },
      (error) => {
        setLoading(false)
        closeModal()
        setNotificationInfo(
          getErrorMessage(error, t),
          'error',
          t('iam:igc_users.remove_user_failed_msg')
        )
      }
    )
  }

  const getModalContent = () => {
    let content
    if (isOwnAccount) {
      content = (
        <Typography type="text" margin={{ top: 'medium' }}>
          {t('iam:igc_users.remove_workspace_user_own_account_not_allowed_msg')}
        </Typography>
      )
    } else if (loading) {
      content = (
        <Box
          pad={{ vertical: 'small', horizontal: 'large' }}
          justify="center"
          align="center"
          fill
        >
          <Loader
            testId="remove-user-loader"
            label={t('common:removing')}
            orientation="horizontal"
          />
        </Box>
      )
    } else {
      content = (
        <Box width="medium" margin={{ top: 'medium' }}>
          <Typography type="text">
            <Trans>
              {isEmpty(userToRemove?.sourceId) && !hasInheritedRoleAssignment
                ? t(
                    isV2London && workspacesCount > 1
                      ? 'iam:igc_users.remove_workspace_user_msg_v2'
                      : 'iam:igc_users.remove_from_workspace_msg_v2',
                    {
                      userName,
                      workspaceName
                    }
                  )
                : t('iam:igc_users.cannot_remove_workspace_user_msg', {
                    userName
                  })}
            </Trans>
          </Typography>
        </Box>
      )
    }
    return content
  }

  return (
    <ModalDialog
      header={
        <ModalHeader
          title={
            <Typography type="heading" level="2">
              {t('iam:igc_users.remove_from_workspace_title')}
            </Typography>
          }
        />
      }
      content={getModalContent()}
      footer={
        !loading && !isOwnAccount && !hasInheritedRoleAssignment ? (
          <ModalFooter
            right={
              <Box direction="row" gap="medium">
                <Button
                  label={t('common:cancel')}
                  onClick={closeModal}
                  testId="remove-user-cancel-btn"
                />
                <Button
                  primary
                  label={t('iam:igc_users.remove_user_btn')}
                  onClick={removeUser}
                  testId="remove-user-remove-btn"
                />
              </Box>
            }
          />
        ) : (
          <ModalFooter
            right={
              <Box direction="row" gap="medium">
                <Button
                  primary
                  label={t('common:close')}
                  onClick={closeModal}
                  testId="remove-user-close-btn"
                />
              </Box>
            }
          />
        )
      }
      onClose={closeModal}
      onClickOutside={closeModal}
      testId="remove-user-modal"
    />
  )
}

RemoveUserModal.propTypes = {
  /**
   * Callback function to close modal
   */
  onSetOpen: PropTypes.func.isRequired,

  /**
   * User to remove
   */
  userToRemove: PropTypes.object.isRequired,

  /**
   * set notification info
   */
  setNotificationInfo: PropTypes.func.isRequired,

  /**
   * role assignments data to check for indirect role assignments
   */
  retrievedData: PropTypes.array.isRequired
}

export default RemoveUserModal
